import * as vis from 'vis-timeline/standalone'
import { toTime, toMillis } from '../../utils/time-utils'
import { addMarkerToTheMap } from '../map/navitime-map'
import { addSpecificPath, deleteMarkersAndPaths, fetchNavitimeDriveDuration } from '../map/route-utils'
import { renderLeafletRoute } from '../map/navitime-map'

/**
 * Alpine.js component factory for timeline visualization and interactions.
 * Mirrors legacy window.tripDataAlpine behavior.
 */
export function tripDataAlpine() {
  return {
    regulation: { isHeavyVehicle: true, type: 'large_special_vehicle', tollType: 'extra_large', dangerousGoods: true },
    visits: [],
    totalTime: '',
    selectedVehicle: { id: 0, name: '' },
    data: [],
    missingDriverData: [],
    violations: [],
    date: '',
    solutionId: 0,
    runId: 0,
    lunchWindowStart: 0,
    lunchWindowEnd: 0,
    allVehiclesSelected: false,
    selectedVehicles: [],
    selectedVehicleTimelineItemData: new vis.DataSet(),
    selectedVehicleTimelineGroupData: new vis.DataSet(),
    selectedVehicleTimeline: null,
    byVehicleItemData: new vis.DataSet(),
    byVehicleGroupData: new vis.DataSet(),
    byVehicleTimeline: null,
    navitimeByVehicleItemData: new vis.DataSet(),
    navitimeByVehicleGroupData: new vis.DataSet(),
    navitimeByVehicleTimeline: null,
    navitimeTimelineLoading: false,
    navitimeTimelineRequestId: 0,

    parseShiftId(groupId) {
      const shiftId = Number(groupId)
      return Number.isFinite(shiftId) ? shiftId : null
    },

    matchTouchesDriver(v, driverId) {
      const drivers = v.subjects?.drivers || []
      return drivers.length === 0 || drivers.some((d) => d.id === driverId)
    },

    matchTouchesVehicle(v, vehicleId) {
      const vehicles = v.subjects?.vehicles || []
      return vehicles.length === 0 || vehicles.some((veh) => veh.id === vehicleId)
    },

    matchTouchesTask(v, driverId, vehicleId, taskIndex) {
      if (!this.matchTouchesDriver(v, driverId) || !this.matchTouchesVehicle(v, vehicleId)) return false
      const orders = v.subjects?.orders || []
      if (orders.length === 0) return false
      return orders.some((order) => order.taskIndex === taskIndex)
    },

    isHardSeverity(v) {
      return (v.severity || '').startsWith('HARD')
    },

    attachTimeline(runDate, items, byVehiclePanel, timelineName, timelineItems, timelineGroups, alpineComponent) {
      let startDate = new Date(`${runDate}T00:00:00`)
      const endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + 1)
      // startDate.setHours(startDate.getHours() - 3)
      const solutionId = this.solutionId
      const runId = this.runId
      const parseShiftId = (groupId) => this.parseShiftId(groupId)
      const byVehicleTimelineOptions = {
        locale: 'ja',
        timeAxis: { scale: 'minute', step: 15 },
        orientation: { axis: 'top' },
        xss: { disabled: true },
        stack: false,
        stackSubgroups: false,
        editable: {
          add: false,
          updateTime: (timelineName === 'byVehicleTimeline'),
          updateGroup: (timelineName === 'byVehicleTimeline'),
          remove: false,
          overrideItems: false
        },
		groupOrder: function (a, b) {
			return b.startMillis - a.startMillis
		},
        selectable: true,
        onMove: function (item, callback) {
		  const token = document.querySelector('input[name="csrf-token"]').value
		  const targetShiftId = parseShiftId(item.group)
		  if (targetShiftId == null) { console.error('Invalid shift group:', item.group); callback(null); return }
		  if(item.data.taskType === 'DEPOT_START') {
			  let data = {
				'csrf-token': token,
				solutionId,
				runId,
				shiftId: targetShiftId,
				startHour: item.start.getHours(),
				startMinute: item.start.getMinutes(),
				isStartTimeChange: true
			  }
			  up.render({ url: '/solution/updateSolution', method: 'POST', target: '#solution-details', failTarget: '#error-details', scroll: 'target', params: data })
				  .then(() => { up.render({ url: `/solution?date=${runDate}&runId=${runId}`, target: '#main', scroll: 'target' }) })
				  .catch((err) => { console.error('Update failed:', err); callback(null) })
			  callback(null)
			  return
		  }
          const oldItems = items.get()
          const oldItem = oldItems.find((oi) => oi.orderId === item.orderId && oi.data?.taskType === item.data?.taskType)
          if (!oldItem) { console.error('Old item not found for orderId:', item.orderId); callback(null); return }

          const sourceShiftId = parseShiftId(oldItem.group)
          if (sourceShiftId == null) { console.error('Invalid source shift group:', oldItem.group); callback(null); return }

          const groupChanged = sourceShiftId !== targetShiftId
          let fromOrdersStr
          if (groupChanged) {
            const oldOrders = oldItems
              .filter((oi) => parseShiftId(oi.group) === sourceShiftId && oi.orderId !== item.orderId && oi.data?.taskType === 'PICKUP')
              .sort((a, b) => a.start.getTime() - b.start.getTime())
              .map((oi) => oi.orderId)
            fromOrdersStr = JSON.stringify(oldOrders.map((orderId, index) => ({ orderId, index })))
          }

          const groupItems = oldItems.filter((i) => i.group === item.group)
          const newGroupItems = groupItems
            .filter((i) => i.orderId !== item.orderId && i.data?.taskType === 'PICKUP')
            .concat(item)
            .sort((a, b) => a.start.getTime() - b.start.getTime())

          const originalIndexMap = {}
          groupItems
            .filter((i) => i.data?.taskType === 'PICKUP')
            .sort((a, b) => a.index - b.index)
            .forEach((itm, idx) => { originalIndexMap[itm.orderId] = idx })

          const toOrders = newGroupItems.map((itm, idx) => ({ orderId: itm.orderId, index: idx, oldIndex: originalIndexMap[itm.orderId] }))
          if (!toOrders.some((o) => o.oldIndex !== o.index)) { console.log('No index changes. Cancelling move.'); callback(null); return }

          const toOrdersStr = JSON.stringify(toOrders.map(({ orderId, index }) => ({ orderId, index })))
          const data = {
            'csrf-token': token,
            solutionId,
            runId,
            shiftId: targetShiftId,
            toShiftId: targetShiftId,
            toOrders: toOrdersStr
          }
          if (groupChanged) {
            data.fromShiftId = sourceShiftId
            data.fromOrders = fromOrdersStr
          }

          up.render({ url: '/solution/updateSolution', method: 'POST', target: '#solution-details', failTarget: '#error-details', scroll: 'target', params: data })
            .then(() => { up.render({ url: `/solution?date=${runDate}&runId=${runId}`, target: '#main', scroll: 'target' }) })
            .catch((err) => { console.error('Update failed:', err); callback(null) })
        },
        horizontalScroll: true,
        verticalScroll: true,
        maxHeight: 1500,
        zoomKey: 'ctrlKey',

        start: startDate,
        end: endDate
      }

      if (timelineName === 'selectedVehicleTimeline') {
        if (this.selectedVehicleTimeline) this.selectedVehicleTimeline.destroy()
      } else if (timelineName === 'navitimeByVehicleTimeline') {
        if (this.navitimeByVehicleTimeline) this.navitimeByVehicleTimeline.destroy()
      } else {
        if (this.byVehicleTimeline) this.byVehicleTimeline.destroy()
      }

      const lunchBackgrounds = []
      const lunchWindowStartOffset = Number(this.lunchWindowStart)
      const lunchWindowEndOffset = Number(this.lunchWindowEnd)
      const lunchWindowDuration = lunchWindowEndOffset - lunchWindowStartOffset
      const runDateStart = new Date(`${runDate}T00:00:00`)
      const dateFromRunOffset = (offsetMillis) => new Date(runDateStart.getTime() + offsetMillis)
      if (Number.isFinite(lunchWindowStartOffset) && Number.isFinite(lunchWindowEndOffset) && lunchWindowDuration > 0) {
        timelineGroups.forEach((group) => {
          const shiftStartItem = timelineItems.get({
            filter: (item) =>
              item.group === group.id &&
              item.data?.timelineElement?.taskType === 'DEPOT_START' &&
              Number.isFinite(Number(item.data.timelineElement.startTime))
          })[0]

          if (!shiftStartItem) return

          const shiftStartOffset = Number(shiftStartItem.data.timelineElement.startTime)
          const lunchStart = dateFromRunOffset(shiftStartOffset + lunchWindowStartOffset)
          const lunchEnd = dateFromRunOffset(shiftStartOffset + lunchWindowStartOffset + lunchWindowDuration)

          lunchBackgrounds.push({
            id: `lunch-${group.id}`,
            group: group.id,
            start: lunchStart,
            end: lunchEnd,
            type: 'background',
            content: '昼休み',
            className: 'lunch-time'
          })
        })
      }
      timelineItems.add(lunchBackgrounds)

      if (timelineName === 'selectedVehicleTimeline') {
        this.selectedVehicleTimeline = new vis.Timeline(byVehiclePanel, timelineItems, timelineGroups, byVehicleTimelineOptions)
      } else if (timelineName === 'navitimeByVehicleTimeline') {
        this.navitimeByVehicleTimeline = new vis.Timeline(byVehiclePanel, timelineItems, timelineGroups, byVehicleTimelineOptions)
      } else {
        this.byVehicleTimeline = new vis.Timeline(byVehiclePanel, timelineItems, timelineGroups, byVehicleTimelineOptions)
      }
      const timelineObject =
        timelineName === 'selectedVehicleTimeline'
          ? this.selectedVehicleTimeline
          : timelineName === 'navitimeByVehicleTimeline'
            ? this.navitimeByVehicleTimeline
            : this.byVehicleTimeline

      timelineObject.on('select', (properties) => {
        if (properties.items.length > 0) {
          const itemId = properties.items[0]
          const item = timelineItems.get(itemId)
          if (item && item.type !== 'background') {
            this.showTimelineItemDetails(item)
            if (timelineObject === this.selectedVehicleTimeline || timelineObject === this.navitimeByVehicleTimeline) {
              deleteMarkersAndPaths()
              if (item.data.timelineElement.taskType === 'DRIVING') {
                addMarkerToTheMap(item.data.from)
                addMarkerToTheMap(item.data.to)
                addSpecificPath(item.data.from, item.data.to, alpineComponent)
                renderLeafletRoute(item.data.from, item.data.to, alpineComponent)
              } else {
                addMarkerToTheMap(item.data.timelineElement.visit)
                alpineComponent.totalTime = ''
                alpineComponent.visits = []
              }
            }
          }
        }
      })

      document.addEventListener('click', (event) => {
        if (!event.target.closest('.vis-item') && !event.target.closest('.popover')) {
          if (window.currentPopover) { window.currentPopover.dispose(); window.currentPopover = null }
        }
      })
    },

    showCompleteTimeLine() {
      this.byVehicleGroupData.clear()
      this.byVehicleItemData.clear()
      const runDate = this.date
      if (this.byVehicleTimeline) { this.byVehicleTimeline.destroy(); this.byVehicleTimeline = null }
      if (!this.selectedVehicles || this.selectedVehicles.length === 0) { return }
      this.data
        .sort((a, b) => `${a.vehicle.romsId}`.localeCompare(`${b.vehicle.romsId}`))
        .forEach((obj) => {
			if (this.selectedVehicles.includes(obj.vehicle.id + '')) {
				this.buildDataForTimeline(obj, runDate, this.byVehicleGroupData, this.byVehicleItemData)
			}
		})
      this.missingDriverData
        .sort((a, b) => `${a.vehicle.romsId}`.localeCompare(`${b.vehicle.romsId}`))
        .forEach((obj) => { if (this.selectedVehicles.includes(obj.vehicle.id + '')) this.buildDataForTimelineButOnlyGroups(obj, runDate, this.byVehicleGroupData) })
      const byVehiclePanel = document.getElementById('byVehiclePanel')
      this.attachTimeline(runDate, this.byVehicleItemData, byVehiclePanel, 'byVehicleTimeline', this.byVehicleItemData, this.byVehicleGroupData)
    },

    getDrivingEndpoints(timeline, index) {
      const prev = timeline[index - 1]
      const next = timeline[index + 1]
      let from
      let to
      if (prev?.taskType === 'LUNCH') from = timeline[index - 2]?.visit
      else from = prev?.visit
      if (next?.taskType === 'LUNCH') to = timeline[index + 2]?.visit
      else to = next?.visit
      return { from, to }
    },
    async recalculateTimelineWithNavitime(obj) {
      const timeline = obj.timeline
      const drivingFetches = []
      let estimateTime = timeline[0]?.startTime ?? 0

      // Pass 1: estimate each driving departure (solver durations), fetch Navitime in parallel
	  // When we parallelize the API calls, there is a trade-off here:
	  // the start_time being passed to Navitime API is approximate not actual
	  // in the context of Navitime as the previous place departure is used from solver given time (estimateTime)
      for (let i = 0; i < timeline.length; i++) {
        const el = timeline[i]
        const backendDuration = el.duration ?? (el.endTime - el.startTime)

        if (el.taskType === 'DRIVING') {
          const { from, to } = this.getDrivingEndpoints(timeline, i)
          if (from && to) {
            const departureTime = estimateTime
            drivingFetches.push(
              fetchNavitimeDriveDuration(from, to, departureTime, this).then((navDuration) => ({ index: i, navDuration }))
            )
          }
        }
        estimateTime += backendDuration
      }

      const navitimeResults = await Promise.all(drivingFetches)
      const navDurationByIndex = new Map()
      for (const { index, navDuration } of navitimeResults) {
        if (navDuration != null) navDurationByIndex.set(index, navDuration)
      }

      // Pass 2: cascade with Navitime driving durations where available
      const adjusted = []
      let currentTime = timeline[0]?.startTime ?? 0

      for (let i = 0; i < timeline.length; i++) {
        const el = timeline[i]
        let duration = el.duration ?? (el.endTime - el.startTime)
        if (el.taskType === 'DRIVING' && navDurationByIndex.has(i)) {
          duration = navDurationByIndex.get(i)
        }

        const startTime = currentTime
        const endTime = currentTime + duration
        adjusted.push({
          ...el,
          startTime,
          endTime,
          duration,
          visit: el.visit
            ? { ...el.visit, arrivalTime: startTime, departureTime: endTime }
            : el.visit
        })
        currentTime = endTime
      }

      return adjusted
    },

    async showNavitimeCompleteTimeLine(vehicleId, alpineComponent) {
	  if (this.navitimeByVehicleTimeline) { this.navitimeByVehicleTimeline.destroy(); this.navitimeByVehicleTimeline = null }
      const requestId = ++this.navitimeTimelineRequestId
      this.navitimeByVehicleGroupData.clear()
      this.navitimeByVehicleItemData.clear()
      if (this.navitimeByVehicleTimeline) {
        this.navitimeByVehicleTimeline.destroy()
        this.navitimeByVehicleTimeline = null
      }
      if (!vehicleId || (vehicleId + '').length === 0) {
        this.navitimeTimelineLoading = false
        return
      }

      this.navitimeTimelineLoading = true
      const runDate = this.date

      try {
        const sortedData = [...this.data].sort((a, b) => `${a.vehicle.romsId}`.localeCompare(`${b.vehicle.romsId}`))
        const selectedShifts = sortedData.filter((obj) => vehicleId === obj.vehicle.id)
        const recalculatedShifts = await Promise.all(
          selectedShifts.map(async (obj) => ({
            obj,
            adjustedTimeline: await this.recalculateTimelineWithNavitime(obj)
          }))
        )
        if (requestId !== this.navitimeTimelineRequestId) return
        for (const { obj, adjustedTimeline } of recalculatedShifts) {
          const drivingMillis = adjustedTimeline
            .filter((t) => t.taskType === 'DRIVING')
            .reduce((sum, t) => sum + t.duration, 0)
          const first = adjustedTimeline[0]
          const last = adjustedTimeline[adjustedTimeline.length - 1]
          const totalWorkMillis = last && first ? last.endTime - first.startTime : obj.totalWorkMillis
          this.buildDataForTimeline(obj, runDate, this.navitimeByVehicleGroupData, this.navitimeByVehicleItemData, {
            timeline: adjustedTimeline,
            drivingMillis,
            totalWorkMillis,
            navitimeAdjusted: true
          })
        }

        const sortedMissing = [...this.missingDriverData].sort((a, b) =>
          `${a.vehicle.romsId}`.localeCompare(`${b.vehicle.romsId}`)
        )
        for (const obj of sortedMissing) {
          if (this.selectedVehicles.includes(obj.vehicle.id + '')) {
            this.buildDataForTimelineButOnlyGroups(obj, runDate, this.navitimeByVehicleGroupData)
          }
        }

        if (requestId !== this.navitimeTimelineRequestId) return
        const navitimePanel = document.getElementById('navitimeByVehiclePanel')
        if (navitimePanel) {
          this.attachTimeline(
            runDate,
            this.navitimeByVehicleItemData,
            navitimePanel,
            'navitimeByVehicleTimeline',
            this.navitimeByVehicleItemData,
            this.navitimeByVehicleGroupData,
			alpineComponent,
          )
        }
      } finally {
        if (requestId === this.navitimeTimelineRequestId) {
          this.navitimeTimelineLoading = false
        }
      }
    },

    buildVehicleTimeline(vehicleId, alpineComponent) {
      this.selectedVehicleTimelineItemData.clear()
      this.selectedVehicleTimelineGroupData.clear()
      const data = this.data.find((obj) => obj.vehicle.id === vehicleId)
      const runDate = this.date
      this.buildDataForTimeline(data, runDate, this.selectedVehicleTimelineGroupData, this.selectedVehicleTimelineItemData)
      const selectedVehiclePanel = document.getElementById('selectedVehicleTimeline')
      this.attachTimeline(runDate, this.selectedVehicleTimelineItemData, selectedVehiclePanel, 'selectedVehicleTimeline', this.selectedVehicleTimelineItemData, this.selectedVehicleTimelineGroupData, alpineComponent)
	  this.showNavitimeCompleteTimeLine(vehicleId, alpineComponent)
    },

    buildDataForTimelineButOnlyGroups(obj, runDate, byVehicleGroupData) {
      let groupDetails = { id: `${obj.vehicle.id < 0 ? '未割当' : obj.vehicle.id} : ${obj.driver.id}`, content: `<h5 title="${obj.driver.id}">${obj.vehicle.id < 0 ? '未割当' : obj.vehicle.romsId} - ${obj.driver.name} - Shift ${obj.shiftId}</h5>` }
      if (obj.vehicle.id > 0) { groupDetails.content += `<br>タンク容量<br>フロント: ${obj.vehicle.frontTankCapacity} <br>リア: ${obj.vehicle.rearTankCapacity} <br> 合計: ${obj.vehicle.totalTankCapacity}` }
      byVehicleGroupData.add(groupDetails)
    },

    buildDataForTimeline(obj, runDate, byVehicleGroupData, byVehicleItemData, options = {}) {
      const timelineElements = options.timeline ?? obj.timeline
      const drivingMillis = options.drivingMillis ?? obj.drivingMillis
      const totalWorkMillis = options.totalWorkMillis ?? obj.totalWorkMillis
      const navitimeAdjusted = options.navitimeAdjusted === true

      const groupId = `${obj.shiftId}`
      const groupName = `${obj.shiftId} ${obj.vehicle.romsId} : ${obj.driver.name}`

      let pickups = timelineElements.filter((visit) => {
        if (!visit.consolidatedOrderIds) return false
        let consolidatedOrderIds = visit.consolidatedOrderIds.split('、')
        return visit.taskType === 'PICKUP' && consolidatedOrderIds.length > 1
      })
      let cssClass = '', consolidated = ''
      if (pickups.length > 0) { cssClass = 'consolidated'; consolidated = `<span>集荷統合: ${pickups.length}</span><br>` }
      for (let i = 0; i < timelineElements.length; i++) {
        let timelineElement = timelineElements[i]
        if (timelineElement.taskType === 'PICKUP') {
          let nextElement = timelineElements[i + 1]
          if (nextElement && nextElement.taskType === 'DRIVING' && nextElement.endTime === timelineElement.endTime) {
            cssClass = 'consolidated'; consolidated = `<span>集荷統合 タイプ1</span><br>`
          }
        }
      }
      if (cssClass === '') {
        let onlyTasks = timelineElements.filter((visit) => ['PICKUP', 'DELIVERY'].includes(visit.taskType))
        for (let i = 0; i < onlyTasks.length; i++) {
          let timelineElement = onlyTasks[i]
          if (timelineElement.taskType === 'PICKUP') {
            let nextElement = onlyTasks[i + 1]
            if (nextElement && nextElement.taskType === 'PICKUP') { cssClass = 'consolidated'; consolidated = `<span>集荷統合 タイプ2</span><br>` }
          }
        }
      }

      const mv = (this.violations || []).find((v) =>
        v.constraintId === 'ドライバーのシフトは重複してはなりません' &&
        this.matchTouchesDriver(v, obj.driver.id)
      )
      const badge = mv ? `<span class="badge bg-danger ms-2">重複割当</span>` : ''
      const waitingLabel = obj.waitingMillis > 0 ? ` | 待機: ${toTime(obj.waitingMillis)}` : ''
      const shiftHours = `<span class="shift-hours">勤務: ${toTime(totalWorkMillis)} | 走行: ${toTime(drivingMillis)}${waitingLabel}</span>`
      byVehicleGroupData.add({ id: groupId, startMillis: obj.startMillis, content: `<h5 title="${obj.driver.id}">Shift ${obj.shiftId}</h5> ${obj.vehicle.romsId} - ${obj.driver.name}  ${badge} ${consolidated} ${shiftHours}<br>タンク容量<br>フロント: ${obj.vehicle.frontTankCapacity} <br>リア: ${obj.vehicle.rearTankCapacity} <br> 合計: ${obj.vehicle.totalTankCapacity}`, className: cssClass })

      for (let i = 0; i < timelineElements.length; i++) {
        let timelineElement = timelineElements[i]
        const itemViolations = (this.violations || []).filter((v) =>
          this.matchTouchesTask(v, obj.driver.id, obj.vehicle.id, timelineElement.taskIndex)
        )
        const hasHard = itemViolations.some((v) => this.isHardSeverity(v))
        const hasMedium = itemViolations.length > 0 && !hasHard
        const violationIcon = hasHard ? '<i class="bi bi-exclamation-octagon-fill text-danger me-1"></i>' : (hasMedium ? '<i class="bi bi-exclamation-triangle-fill text-warning me-1"></i>' : '')

        let orderId = timelineElement.romsOrderId
        let itemName = ''
        if (timelineElement.visit) itemName = timelineElement.visit?.job?.item
        if (['DEPOT_START', 'DEPOT_END', 'DRIVING', 'CLEANING', 'WAITING', 'LUNCH'].includes(timelineElement.taskType)) { orderId = ''; itemName = '' }

        let setTimeUpdated = '', setTime = ''
        if (timelineElement.taskType === 'DELIVERY') {
          if (timelineElement.setTimeUpdated) setTimeUpdated = timelineElement.setTimeUpdated
          if (timelineElement.setTime) setTime = timelineElement.setTime
        }

        let setTimeStart = 0, setTimeEnd = 0, timeWindowViolation = ''
        if (setTimeUpdated) {
          setTimeStart = toMillis(setTimeUpdated.split('~')[0])
          setTimeEnd = toMillis(setTimeUpdated.split('~')[1])
          if (setTimeStart > timelineElement.startTime || setTimeEnd < timelineElement.endTime) {
			  if (setTimeStart < (timelineElement.startTime + 60 * 60 * 1000) && setTimeEnd > (timelineElement.endTime - 60 * 60 * 1000)) {
				  timeWindowViolation = 'yellow'
			  } else {
				  timeWindowViolation = 'red'
			  }
          }
        }

        let consolidatedOrderIds = []
        if (timelineElement.consolidatedOrderIds) consolidatedOrderIds = timelineElement.consolidatedOrderIds.split('、')

		  const isPickupOrDelivery =
			  ['PICKUP', 'DELIVERY'].includes(timelineElement.taskType);

		  const showItemName =
			  isPickupOrDelivery && itemName;

		  const showTank =
			  isPickupOrDelivery &&
			  timelineElement.orderId &&
			  timelineElement.assignedTank &&
			  consolidatedOrderIds.length <= 1;

		  const showConsolidatedOrders =
			  isPickupOrDelivery &&
			  consolidatedOrderIds.length > 1;

		  const showVisitName =
			  isPickupOrDelivery &&
			  timelineElement.visit?.name;

		  const showVisitAddress =
			  isPickupOrDelivery &&
			  timelineElement.visit?.address;

		  const showVolume =
			  isPickupOrDelivery &&
			  timelineElement.visit?.volume1;

		  const showSetTime =
			  isPickupOrDelivery &&
			  setTime;

		  const showSetTimeUpdated =
			  isPickupOrDelivery &&
			  setTimeUpdated;

		  const violationMessage =
			  timeWindowViolation === 'red'
				  ? '指定時間違反'
				  : timeWindowViolation === 'yellow'
					  ? '指定時間違反（ただし、少し近い）'
					  : '';

		  let content = `
    <div>
        <span>

            ${timelineElement.taskTypeJapanese}
            ${orderId ? `<span>${orderId}</span>` : ''}
        </span>
        <br>

        ${showItemName
			  ? `品名：${itemName}<br>`
			  : ''}

        ${showTank
			  ? `<span>タンク: ${timelineElement.assignedTank}</span><br>`
			  : ''}

        ${showConsolidatedOrders
			  ? `<span>統合受注： ${timelineElement.consolidatedOrderIds}</span><br>`
			  : ''}

        ${showVisitName
			  ? `<span>${timelineElement.visit.name}</span><br>`
			  : ''}

        ${showVisitAddress
			  ? `<span>${timelineElement.visit.address}</span><br>`
			  : ''}

        ${showVolume
			  ? `<span>量: ${timelineElement.visit.volume1} ${timelineElement.visit.unit1}</span><br>`
			  : ''}

        ${showSetTime
			  ? `<span>指定時間 (ROMS): ${setTime}</span><br>`
			  : ''}

        ${showSetTimeUpdated
			  ? `<span>指定時間: ${setTimeUpdated}</span><br>`
			  : ''}

        ${violationMessage
			  ? `${violationMessage}<br>`
			  : ''}

        <span>期間: ${toTime(timelineElement.duration)}</span><br>
        <span>${toTime(timelineElement.startTime)} ~ ${toTime(timelineElement.endTime)}</span>
    </div>
`;

        const start = new Date(runDate + 'T' + toTime(timelineElement.startTime))
        const end = new Date(runDate + 'T' + toTime(timelineElement.endTime))
        let taskTypeClass = `task-${timelineElement.taskType.toLowerCase()}`
        if (navitimeAdjusted && timelineElement.taskType === 'DRIVING') taskTypeClass += ' navitime-adjusted'
        if (consolidatedOrderIds.length > 1) taskTypeClass += ' consolidated'
        if (timeWindowViolation === 'red') taskTypeClass += ' time-window-violation'
        else if (timeWindowViolation === 'yellow') taskTypeClass += ' time-window-violation-yellow'
        if (hasHard) taskTypeClass += ' violation-hard'
        else if (hasMedium) taskTypeClass += ' violation-medium'
        if (timelineElement.taskType === 'LUNCH' && timelineElement.lunchType.indexOf('WHILE') > -1) taskTypeClass = 'task-lunch-overlap'

        const data = {
          group: groupId,
          groupName,
          content,
          start,
          end,
          editable: timelineElement.taskType === 'PICKUP' || timelineElement.taskType === 'DEPOT_START',
          remove: false,
          index: timelineElement.taskIndex,
          orderId: timelineElement.orderId,
          romsOrderId: timelineElement.romsOrderId,
          vehicleId: obj.vehicle.id,
          driverId: obj.driver.id,
          className: taskTypeClass,
          data: {
            orderId,
            timeWindowViolation,
            itemName,
            taskType: timelineElement.taskType,
            timelineElement: timelineElement,
            vehicle: obj.vehicle,
            driver: obj.driver,
            violations: itemViolations
          }
        }

	  if (timelineElement.taskType === 'DRIVING') {
		  const { from, to } = this.getDrivingEndpoints(timelineElements, i)
		  data.data.from = from
		  data.data.to = to
	  }

        byVehicleItemData.add(data)
      }
    },

    showTimelineItemDetails(item) {
      if (window.currentPopover) { window.currentPopover.dispose(); window.currentPopover = null }
      const data = item.data, timelineElement = data.timelineElement
      const timeRange = `${toTime(timelineElement.startTime)} ~ ${toTime(timelineElement.endTime)}`
      const duration = toTime(timelineElement.duration)
      const details = []
      let consolidatedOrderIds = []
      if (timelineElement.consolidatedOrderIds) consolidatedOrderIds = timelineElement.consolidatedOrderIds.split('、')
      const isConsolidated = consolidatedOrderIds.length > 1
      if (data.itemName) details.push(`<i class="bi bi-box me-2 text-info"></i>: ${data.itemName}`)
      if (consolidatedOrderIds.length > 1) details.push(`<i class="bi bi-file-text me-2 text-info"></i>: 統合受注「${timelineElement.consolidatedOrderIds}」`)
      if (timelineElement.visit?.name) details.push(`<i class="bi bi-signpost me-2 text-info"></i>: ${timelineElement.visit.name}`)
      if (timelineElement.visit?.address) details.push(`<i class="bi bi-geo-alt-fill me-2 text-info"></i>: ${timelineElement.visit.address}`)
      if (timelineElement.taskType === 'PICKUP' || timelineElement.taskType === 'DELIVERY') {
        details.push(`<i class="bi bi-box-fill me-2 text-info"></i>: ${timelineElement.visit.job.volume1} ${timelineElement.visit.job.unit1}`)
        if (timelineElement.visit?.job.volume2 && timelineElement.visit?.job.unit2) details.push(`<i class="bi bi-cup me-2 text-info"></i>: ${timelineElement.visit.job.volume2} ${timelineElement.visit.job.unit2}`)
        if (['PICKUP', 'DELIVERY'].includes(timelineElement.taskType) && timelineElement.orderId && timelineElement.assignedTank && consolidatedOrderIds.length <= 1) details.push(`<i class="bi bi-droplet me-2 text-info"></i>: タンク: ${timelineElement.assignedTank}`)
      }
      if (timelineElement.taskType === 'DELIVERY') {
        if (timelineElement.setTime) details.push(`<i class="bi bi-clock me-2 text-warning"></i>: ${timelineElement.setTime} (ROMS)`)
        if (timelineElement.setTimeUpdated) details.push(`<i class="bi bi-clock me-2 text-warning"></i>: ${timelineElement.setTimeUpdated}`)
        if (data.timeWindowViolation === 'red') details.push(`<i class="bi bi-exclamation-circle me-2 text-danger"></i>: 指定時間違反`)
        else if (data.timeWindowViolation === 'yellow') details.push(`<i class="bi bi-exclamation-circle me-2 text-warning"></i>: 指定時間違反（ただし、少し近い）`)
      }

      const itemViolations = item.data.violations || []
      let popoverViolation = '', className = '', icon = '<i class="bi bi-info-circle-fill me-2"></i>'
      if (data.timeWindowViolation === 'red') { popoverViolation = '(指定時間違反)'; className = 'text-danger background-white'; icon = '<i class="bi bi-exclamation-circle me-2"></i>' }
      else if (data.timeWindowViolation === 'yellow') { popoverViolation = '(指定時間違反、ただし、少し近い）'; className = 'text-warning background-white'; icon = '<i class="bi bi-exclamation-circle me-2"></i>' }
      const popoverContent = `
        <div class="timeline-detail-popover">
          <div class="popover-header-custom">
            <h6 class="mb-0 ${className}">
              ${icon}
              ${timelineElement.taskTypeJapanese}${popoverViolation}
              ${isConsolidated ? '<span class="badge bg-success ms-2">統合</span>' : ''}
            </h6>
          </div>
          <div class="popover-body-custom">
            ${item.orderId ? `
              <div class="info-section">
                <div class="info-row">
                  <i class="bi bi-file-text me-2 text-info"></i>
                  <strong>受注:</strong> ${item.romsOrderId}
                </div>
              </div>
            ` : ''}
            <div class="info-section">
              <div class="info-row"><i class="bi bi-truck me-2 text-info"></i><strong>車両:</strong> ${data.vehicle.romsId}</div>
              <div class="info-row"><i class="bi bi-person me-2 text-warning"></i><strong>ドライバー:</strong> ${data.driver.name}</div>
            </div>
            ${details.length > 0 ? `
              <div class="info-section">
                <div class="info-header">
                  <i class="bi bi-list-ul me-2 text-secondary"></i>
                  <strong>詳細情報</strong>
                </div>
                ${details.map((detail) => `<div class="info-detail">${detail}</div>`).join('')}
              </div>
            ` : ''}

            <div class="info-section">
              <div class="info-row">
                <i class="bi bi-clock me-2 text-primary"></i>
                <strong>時間:</strong> ${timeRange}
              </div>
              <div class="info-row">
                <i class="bi bi-hourglass-split me-2 text-primary"></i>
                <strong>期間:</strong> ${duration}
              </div>
            </div>
          </div>
        </div>
      `

      setTimeout(() => {
        const timelineEl = document.querySelector('.vis-item.vis-selected') || document.querySelector(`[data-item="${item.id}"]`)
        if (timelineEl) {
          window.currentPopover = new bootstrap.Popover(timelineEl, { content: popoverContent, html: true, placement: 'auto', trigger: 'manual', container: 'body', customClass: 'timeline-detail-popover-container', sanitize: false })
          window.currentPopover.show()
        }
      }, 50)
    },

    init() {
      this.data = up.data(document.querySelector('#data'))
      this.missingDriverData = up.data(document.querySelector('#missing-driver-data')) || []
      const raw = up.data(document.querySelector('#violations-data'))
      this.violations = Array.isArray(raw) ? raw : (raw?.matches || [])
      const solutionInfo = document.querySelector('#solution-info').dataset
      this.solutionId = +solutionInfo.solutionId
      this.runId = +solutionInfo.runId
      this.date = solutionInfo.date
      this.lunchWindowStart = solutionInfo.lunchWindowStart
      this.lunchWindowEnd = solutionInfo.lunchWindowEnd
      this.$watch('selectedVehicles', (value) => {
        this.allVehiclesSelected = value.length === this.data.length
        this.showCompleteTimeLine()
      })
      window.alpineComponent = this
    },

    selectOrDeselectAll() {
      this.selectedVehicles = this.selectedVehicles.length === this.data.length ? [] : this.data.map((obj) => obj.vehicle.id + '')
      this.allVehiclesSelected = this.selectedVehicles.length === this.data.length
    },

    onVehicleChange(vehicle) {
      this.selectedVehicle = vehicle
      this.visits = []
      this.totalTime = ''
      deleteMarkersAndPaths()
      const selectedVehicleData = this.data.find((obj) => obj.vehicle.id === this.selectedVehicle.id)
      if (!selectedVehicleData) return
      this.buildVehicleTimeline(this.selectedVehicle.id, this)
    }
  }
}

export default tripDataAlpine
